<template>
    <admin-page :result="result" description="Errores reportados por la app, agrupados por error y día (hora de Chile)">
        <div class="err-toolbar">
            <kimsa-combobox class="err-select" :items="dayOptions" :value="days" item-value="value"
                @change="onChangeDays" dense solo no-object />
            <kimsa-text-field class="err-search" @change="search = $event" :value="search"
                placeholder="Filtrar por error, ruta o versión" prepend-inner-icon="mdi-magnify" dense hide-details />
            <v-btn icon small class="err-refresh" :loading="loading" @click="loadLogs">
                <v-icon size="18">mdi-refresh</v-icon>
            </v-btn>
            <v-btn text small color="error" class="err-clear" :disabled="loading || !filtered.length"
                @click="$refs.confirmClear.show()">
                <v-icon left size="16">mdi-broom</v-icon>
                Limpiar {{ filtered.length }}
            </v-btn>
        </div>

        <div v-if="!loading && (showPlatformChips || showBuildChips)" class="err-chips">
            <chip-selector v-if="showPlatformChips" label="Plataforma" :items="platformOptions"
                :value="platform" @change="platform = $event" />
            <chip-selector v-if="showBuildChips" label="Build" :items="buildOptions" :value="build"
                @change="build = $event" />
        </div>

        <skeleton-loader v-if="loading" type="rows" :count="6" class="mt-4" />

        <div v-else-if="paged.length" class="err-list">
            <div v-for="log in paged" :key="log.id" class="err-item">
                <div class="err-row" @click="toggle(log.id)">
                    <span class="err-count">{{ log.count || 1 }}</span>
                    <div class="err-body">
                        <div class="err-message">{{ log.message }}</div>
                        <div class="err-meta">
                            {{ log.route || 'sin ruta' }} · {{ log.platform }} · {{ log.version }} ·
                            {{ dayLabel(log) }} {{ lastSeenLabel(log) }}
                        </div>
                    </div>
                    <v-icon size="18" color="txtMuted">
                        {{ expanded === log.id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>
                </div>

                <div v-if="expanded === log.id" class="err-detail">
                    <div class="err-detail-row"><span>Archivo</span><b>{{ log.file }}:{{ log.line }}</b></div>
                    <div class="err-detail-row"><span>Usuario</span><b>{{ log.email }} ({{ log.accountType }})</b></div>
                    <div class="err-detail-row"><span>UID</span><b>{{ log.uid }}</b></div>
                    <div class="err-detail-row"><span>Origen</span><b>{{ log.source }}</b></div>
                    <div class="err-detail-row"><span>Parámetros</span><b>{{ log.params }}</b></div>
                    <div v-if="log.moreInfo && log.moreInfo !== 'n/d'" class="err-block">{{ log.moreInfo }}</div>
                    <pre class="err-stack">{{ log.stack }}</pre>
                    <v-btn text small color="error" class="err-delete" @click="showDelete(log)">
                        <v-icon left size="16">mdi-trash-can-outline</v-icon>
                        Borrar registro
                    </v-btn>
                </div>
            </div>
        </div>

        <div v-else class="err-empty">
            <v-icon size="34" color="txtMuted">mdi-shield-check-outline</v-icon>
            <span>Sin errores en el período.</span>
        </div>

        <kimsa-pagination v-if="!loading && pageCount > 1" class="mt-4" center :page-count="pageCount"
            :value="page" :total-visible="7" @page="page = $event" />

        <kimsa-confirm ref="confirm" alert-mode show-cancel title="Borrar registro"
            content="Se borra este registro de errores. No afecta a la app." :persistent="false"
            :close-on-confirm="false" :loading="deleting" @confirm="deleteLog" />

        <kimsa-confirm ref="confirmClear" alert-mode show-cancel title="Limpiar errores" :content="clearContent"
            :persistent="false" :close-on-confirm="false" :loading="clearing" @confirm="clearFiltered" />
    </admin-page>
</template>

<script>
import KimsaTextField from '@/components/common/inputs/KimsaTextField.vue'
import KimsaCombobox from '@/components/common/inputs/KimsaCombobox.vue'
import KimsaConfirm from '@/components/common/KimsaConfirm.vue'
import ChipSelector from '@/components/common/inputs/ChipSelector.vue'
import KimsaPagination from '@/components/common/KimsaPagination.vue'
import SkeletonLoader from '@/components/common/SkeletonLoader.vue'
import AdminPage from '@/components/admin/AdminPage.vue'
import { db } from '@/utils/Firebase'
import { adminRouteGuard } from '@/utils/Admin'

const PLATFORM_LABELS = { web: 'Web', ios: 'iOS', android: 'Android', 'n/d': 'Sin plataforma' }
const PER_PAGE = 20
const BATCH_SIZE = 400
const CHILE_TZ = 'America/Santiago'
const CHILE_DATE = new Intl.DateTimeFormat('es-CL', {
    timeZone: CHILE_TZ, day: '2-digit', month: '2-digit', year: 'numeric',
})
const CHILE_TIME = new Intl.DateTimeFormat('es-CL', {
    timeZone: CHILE_TZ, hour: '2-digit', minute: '2-digit', hour12: false,
})

export default {
    name: 'AdminErrors',
    components: { KimsaTextField, KimsaCombobox, KimsaConfirm, SkeletonLoader, AdminPage, ChipSelector, KimsaPagination },
    beforeRouteEnter: adminRouteGuard,
    data() {
        return {
            logs: [],
            search: '',
            platform: '',
            build: '',
            page: 1,
            days: 7,
            dayOptions: [
                { name: 'Últimos 7 días', value: 7 },
                { name: 'Últimos 15 días', value: 15 },
                { name: 'Últimos 30 días', value: 30 },
            ],
            expanded: null,
            loading: false,
            deleting: false,
            clearing: false,
            pending: null,
            result: null,
        }
    },
    computed: {
        searched(vm = this) {
            const search = vm.search.trim().toLowerCase()
            if (!search) return vm.logs
            return vm.logs.filter(log => `${log.message} ${log.route} ${log.version} ${log.file}`
                .toLowerCase().includes(search))
        },
        platformOptions(vm = this) {
            const logs = vm.searched.filter(log => !vm.build || vm.buildOf(log) === vm.build)
            return vm.chipOptions(logs, log => log.platform || 'n/d', key => vm.platformLabel(key), 'Todas',
                (keys, counters) => keys.sort((a, b) => counters[b] - counters[a]))
        },
        buildOptions(vm = this) {
            const logs = vm.searched.filter(log => !vm.platform || (log.platform || 'n/d') === vm.platform)
            return vm.chipOptions(logs, log => vm.buildOf(log),
                key => key === 'n/d' ? 'Sin build' : `Build ${key}`, 'Todos',
                keys => keys.sort((a, b) => Number(b) - Number(a)))
        },
        showPlatformChips(vm = this) {
            return vm.platformOptions.length > 2 || !!vm.platform
        },
        showBuildChips(vm = this) {
            return vm.buildOptions.length > 2 || !!vm.build
        },
        filtered(vm = this) {
            return vm.searched.filter(log => {
                if (vm.platform && (log.platform || 'n/d') !== vm.platform) return false
                if (vm.build && vm.buildOf(log) !== vm.build) return false
                return true
            })
        },
        activeFilterLabel(vm = this) {
            const parts = []
            if (vm.platform) parts.push(vm.platformLabel(vm.platform))
            if (vm.build) parts.push(vm.build === 'n/d' ? 'sin build' : `build ${vm.build}`)
            if (vm.search.trim()) parts.push(`"${vm.search.trim()}"`)
            return parts.length ? parts.join(' · ') : `todo el período (${vm.days} días)`
        },
        clearContent(vm = this) {
            return `Se borran ${vm.filtered.length} registros de ${vm.activeFilterLabel}, no solo los de esta página. No afecta a la app.`
        },
        pageCount(vm = this) {
            return Math.ceil(vm.filtered.length / PER_PAGE)
        },
        paged(vm = this) {
            const start = (vm.page - 1) * PER_PAGE
            return vm.filtered.slice(start, start + PER_PAGE)
        },
    },
    mounted() {
        this.loadLogs()
    },
    watch: {
        search() { this.page = 1 },
        pageCount(val) { if (this.page > val) this.page = Math.max(1, val) },
        platform() {
            this.page = 1
            if (this.build && !this.buildOptions.some(option => option.value === this.build)) this.build = ''
        },
        build() {
            this.page = 1
            if (this.platform && !this.platformOptions.some(option => option.value === this.platform)) this.platform = ''
        },
    },
    methods: {
        platformLabel(platform) {
            return PLATFORM_LABELS[platform] || platform
        },
        chipOptions(logs, keyOf, labelOf, allLabel, sortKeys) {
            const counters = {}
            logs.forEach(log => {
                const key = keyOf(log)
                counters[key] = (counters[key] || 0) + 1
            })
            const options = sortKeys(Object.keys(counters), counters)
                .map(key => ({ value: key, label: `${labelOf(key)} (${counters[key]})` }))
            return [{ value: '', label: `${allLabel} (${logs.length})` }, ...options]
        },
        buildOf(log) {
            const match = String(log.version || '').match(/app\s+(\d+)/)
            return match ? match[1] : 'n/d'
        },
        lastSeenDate(log) {
            if (!log.lastSeen) return null
            return log.lastSeen.toDate ? log.lastSeen.toDate() : log.lastSeen
        },
        dayLabel(log) {
            const date = this.lastSeenDate(log)
            if (date) return CHILE_DATE.format(date)
            const parts = String(log.day || '').split('-')
            return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : (log.day || '')
        },
        lastSeenLabel(log) {
            const date = this.lastSeenDate(log)
            if (!date) return ''
            return `· última ${CHILE_TIME.format(date)}`
        },
        onChangeDays(days) {
            this.days = Number(days) || 7
            this.page = 1
            this.loadLogs()
        },
        toggle(id) {
            this.expanded = this.expanded === id ? null : id
        },
        async loadLogs() {
            const vm = this
            vm.result = null
            await vm.withLoading(async () => {
                const from = vm.$moment().subtract(vm.days - 1, 'days').format('YYYY-MM-DD')
                const snapshot = await db.collection('errorLogs')
                    .where('day', '>=', from)
                    .get()

                vm.logs = snapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .sort((a, b) => {
                        if (b.day !== a.day) return b.day.localeCompare(a.day)
                        return (b.count || 0) - (a.count || 0)
                    })
            })
        },
        showDelete(log) {
            this.pending = log
            this.$refs.confirm.show()
        },
        async clearFiltered() {
            const vm = this
            const ids = vm.filtered.map(log => log.id)
            if (!ids.length) return vm.$refs.confirmClear.hide()
            vm.clearing = true
            try {
                for (let i = 0; i < ids.length; i += BATCH_SIZE) {
                    const batch = db.batch()
                    ids.slice(i, i + BATCH_SIZE).forEach(id => batch.delete(db.collection('errorLogs').doc(id)))
                    await batch.commit()
                }
                const removed = new Set(ids)
                vm.logs = vm.logs.filter(log => !removed.has(log.id))
                vm.page = 1
                vm.expanded = null
                vm.result = { type: 'success', message: `${ids.length} registros borrados.` }
            } catch (err) {
                console.error('[adminErrors] clear', err)
                vm.result = { type: 'error', message: err.message || 'Error al borrar los registros.' }
            } finally {
                vm.clearing = false
                vm.$refs.confirmClear?.hide()
            }
        },
        async deleteLog() {
            const vm = this
            vm.deleting = true
            try {
                await db.collection('errorLogs').doc(vm.pending.id).delete()
                vm.logs = vm.logs.filter(log => log.id !== vm.pending.id)
                vm.result = { type: 'success', message: 'Registro borrado.' }
            } catch (err) {
                console.error('[adminErrors] delete', err)
                vm.result = { type: 'error', message: err.message || 'Error al borrar el registro.' }
            } finally {
                vm.deleting = false
                vm.pending = null
                vm.$refs.confirm?.hide()
            }
        },
    },
}
</script>

<style scoped>
.err-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.err-select {
    width: 200px;
}

.err-search {
    flex: 1;
    min-width: 200px;
}

.err-refresh {
    color: var(--v-txtMuted-base);
}

.err-clear {
    text-transform: none !important;
    letter-spacing: normal !important;
}

.err-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-top: 14px;
}

.err-list {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.err-item {
    border-radius: 10px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base);
    overflow: hidden;
}

.err-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    cursor: pointer;
}

.err-row:hover {
    background: var(--v-surface-base);
}

.err-count {
    min-width: 34px;
    height: 22px;
    padding: 0 8px;
    border-radius: 11px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(232, 92, 92, 0.14);
    color: var(--v-error-base);
    font-size: 11px;
    font-weight: 800;
    flex-shrink: 0;
}

.err-body {
    flex: 1;
    min-width: 0;
}

.err-message {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.err-meta {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    margin-top: 2px;
}

.err-detail {
    padding: 12px;
    border-top: 1px solid var(--v-outline-base);
    background: var(--v-surface-base);
}

.err-detail-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    font-size: 12px;
    color: var(--v-txtMuted-base);
    padding: 3px 0;
}

.err-detail-row b {
    color: var(--v-txtPrimary-base);
    font-weight: 600;
    text-align: right;
    word-break: break-all;
}

.err-block,
.err-stack {
    margin-top: 10px;
    padding: 10px;
    border-radius: 8px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    font-size: 11px;
    line-height: 1.5;
    color: var(--v-txtSecondary-base);
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 260px;
    overflow-y: auto;
}

.err-delete {
    margin-top: 10px;
    text-transform: none;
    letter-spacing: normal;
}

.err-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 32px 12px;
    color: var(--v-txtMuted-base);
    font-size: 14px;
}
</style>
